import { useEffect, useState, useCallback } from "react";
import api, { getLevel } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, DollarSign } from "lucide-react";

// Brand color #3556A5
const BRAND = "#3556A5";

const FIELDS = [
  ["additions", "Additions", "currency"],
  ["deductions", "Deductions", "currency"],
  ["bonus", "Bonus", "currency"],
  ["staff_referral_bonus", "Staff Referral Bonus", "currency"],
  ["biweekly_bonus_1_amount", "Biweekly Bonus #1 Amount", "currency"],
  ["biweekly_bonus_2_amount", "Biweekly Bonus #2 Amount", "currency"],
  ["biweekly_bonus_3_amount", "Biweekly Bonus #3 Amount", "currency"],
  ["biweekly_bonus_4_amount", "Biweekly Bonus #4 Amount", "currency"],
  ["perfect_attendance_bonus_amount", "Perfect Attendance Bonus Amount", "currency"],
  ["payment", "Payment", "text"],
];

const fmtTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function PayrollPanel({ crewId, crewName }) {
  const isLeadership = getLevel() === "leadership";
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [payroll, setPayroll] = useState({});
  const [saving, setSaving] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/admin/crew-members/${crewId}/payroll`);
      setPayroll(r.data?.payroll || {});
      setLoaded(true);
    } catch (e) {
      if (e?.response?.status === 403) return;  // not leadership
      toast.error(e?.response?.data?.detail || "Failed to load payroll");
    }
  }, [crewId]);

  useEffect(() => { if (open && !loaded && isLeadership) refresh(); }, [open, loaded, isLeadership, refresh]);

  if (!isLeadership) return null;

  const saveField = async (field, value) => {
    setSaving((s) => ({ ...s, [field]: true }));
    try {
      const body = { [field]: value };
      const r = await api.put(`/admin/crew-members/${crewId}/payroll`, body);
      setPayroll(r.data?.payroll || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to save ${field}`);
    } finally {
      setSaving((s) => ({ ...s, [field]: false }));
    }
  };

  const onBlurNum = (field) => (e) => {
    const raw = (e.target.value || "").trim();
    const next = raw === "" ? null : Number(raw.replace(/[^0-9.\-]/g, ""));
    if (next !== null && Number.isNaN(next)) {
      toast.error(`${field}: not a number`);
      return;
    }
    if ((payroll[field] ?? null) !== (next ?? null)) saveField(field, next);
  };

  const onBlurText = (field) => (e) => {
    const next = e.target.value || "";
    if ((payroll[field] || "") !== next) saveField(field, next || null);
  };

  return (
    <section className="rounded-2xl border-2 overflow-hidden mb-4" style={{ borderColor: BRAND }} data-testid="payroll-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-white font-bold"
        style={{ backgroundColor: BRAND }}
        data-testid="payroll-toggle"
      >
        <span className="flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Payroll
          <span className="ml-2 text-[10px] uppercase tracking-[0.16em] bg-white/20 px-2 py-0.5 rounded">Leadership only</span>
        </span>
        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {open && (
        <div className="p-4 bg-white space-y-3" data-testid="payroll-fields">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Salary first */}
            <div data-testid="payroll-field-total_salary">
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-600 font-bold">Total Salary</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="number" step="0.01" min="0"
                  defaultValue={payroll.total_salary ?? ""}
                  onBlur={onBlurNum("total_salary")}
                  placeholder="0.00"
                  className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2"
                  style={{ '--tw-ring-color': BRAND }}
                  data-testid="payroll-input-total_salary"
                  disabled={!!saving.total_salary}
                />
              </div>
            </div>
            {/* Split paycheck toggle */}
            <div data-testid="payroll-field-split_paycheck">
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-600 font-bold">Split Paycheck</label>
              <div className="flex gap-2 mt-1">
                {[
                  ["1_check", "1 Check"],
                  ["2_checks", "2 Checks"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => saveField("split_paycheck", k)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border-2 transition ${
                      (payroll.split_paycheck || "1_check") === k
                        ? "text-white"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                    style={(payroll.split_paycheck || "1_check") === k ? { backgroundColor: BRAND, borderColor: BRAND } : {}}
                    data-testid={`split-${k}`}
                    disabled={!!saving.split_paycheck}
                  >{label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map(([f, label, kind]) => (
              <div key={f} data-testid={`payroll-field-${f}`}>
                <label className="text-[10px] uppercase tracking-[0.16em] text-slate-600 font-bold">{label}</label>
                {kind === "currency" ? (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <input
                      key={`${f}-${payroll.last_updated_at || "init"}`}
                      type="number" step="0.01"
                      defaultValue={payroll[f] ?? ""}
                      onBlur={onBlurNum(f)}
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2"
                      data-testid={`payroll-input-${f}`}
                      disabled={!!saving[f]}
                    />
                  </div>
                ) : (
                  <input
                    key={`${f}-${payroll.last_updated_at || "init"}`}
                    type="text"
                    defaultValue={payroll[f] ?? ""}
                    onBlur={onBlurText(f)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2"
                    data-testid={`payroll-input-${f}`}
                    disabled={!!saving[f]}
                  />
                )}
              </div>
            ))}
          </div>

          <div data-testid="payroll-field-notes">
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-600 font-bold">Notes</label>
            <textarea
              key={`notes-${payroll.last_updated_at || "init"}`}
              rows={3}
              defaultValue={payroll.notes ?? ""}
              onBlur={onBlurText("notes")}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2"
              data-testid="payroll-input-notes"
              disabled={!!saving.notes}
            />
          </div>

          <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-100" data-testid="payroll-last-updated">
            {payroll.last_updated_at
              ? <>Last updated: <strong>{fmtTime(payroll.last_updated_at)}</strong> by <strong>{payroll.last_updated_by || "—"}</strong></>
              : "No payroll data on file yet."
            }
            {crewName && <span className="ml-2 text-slate-400">· {crewName}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
